<template>
  <dialog ref="dialog" class="gam-dialog" @close="hideDialog" @click="handleBackdropClick">
    <transition name="fade">
      <div v-if="visible" class="gam-dialog-modal" :class="{ prevent: preventClose, mobile: isMobile, bg: background }">
        <div
          disabled-v-on-click-outside="closeModal"
          :class="['gam-dialog-content', { 'has-close-button': !hideBack }]"
        >
          <div class="bg-image hue-rotate" :style="getBgStyle" />

          <slot />

          <div v-if="!preventClose && !hideBack" class="back-button" @click="handleClickClose">
            <GamIcon :size="GamIconSize.NORMAL" :name="GamIconName.X_CLOSE" is-dark />
          </div>
        </div>
      </div>
    </transition>
  </dialog>
</template>

<script setup lang="ts">
import { isMobile } from '@/core/helpers/ui.helper';
import { useAppStore } from '@/stores/app.store';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import type { DialogType } from '@/views/composables/models/dialog.interface';
import { computed, ref, type StyleValue } from 'vue';
import GamIcon from '../GamIcon.vue';

const props = defineProps<DialogType>();
const emits = defineEmits([GamComponentsEmits.CLOSE]);
const dialog = ref<HTMLDialogElement>();
const visible = ref(false);
const appStore = useAppStore();

const showModal = () => {
  dialog.value?.showModal();

  if (dialog.value) {
    dialog.value?.addEventListener('cancel', (event) => {
      if (props.preventClose) {
        event.preventDefault();
        // event.stopPropagation();
      }
    });
  }
  appStore.setDialogOpen(true);
  visible.value = true;
};

const closeModal = (): void => {
  if (!props.preventClose) {
    dialog.value?.close();
  }
};

const hideDialog = (): void => {
  appStore.setDialogOpen(false);
  visible.value = false;
  emits(GamComponentsEmits.CLOSE);
};

const handleClickClose = () => {
  hideDialog();
  closeModal();
};

const handleBackdropClick = (event: MouseEvent) => {
  if (props.preventClose) return;
  if (event.target === dialog.value) {
    handleClickClose();
  }
};

const getBgStyle = computed((): StyleValue => {
  const bg = props.background ? `/images/dialogs/${props.background}` : undefined;
  return {
    backgroundImage: `url(${bg})`,
  };
});

defineExpose({
  show: showModal,
  close: closeModal,
  visible,
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

.gam-dialog {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  outline: 0;

  &::-webkit-scrollbar {
    display: none;
  }
  background: none;
  border: 0;
  margin: 0 auto;
  transition: visibility var(--transition-loader);
  color: var(--color-white-100);
  position: relative;
}

.gam-dialog::backdrop {
  background: rgba(23, 23, 23, 0.7);
}

.bg-image {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center -54px;
  background-color: var(--color-dark-700);
  background-color: var(--color-dark-600);
  border-radius: var(--radius-large);
}

.gam-dialog-modal {
  display: flex;
  position: relative;
  width: auto;
  max-width: 500px;
  min-height: 100%;
  pointer-events: none;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.gam-dialog-modal.mobile {
  max-width: 88vw;
}

.gam-dialog-modal.bg {
  .gam-dialog-content {
    min-height: 600px;
  }
}

.gam-dialog-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: fit-content;
  pointer-events: auto;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  padding: var(--spacing-xl) var(--spacing-xl) var(--spacing-xxl);
  border-radius: var(--radius-large);
  background: var(--color-dark-700);
  background-clip: padding-box;
  background-size: cover !important;
  z-index: 100;
  box-shadow: 0px 0px var(--radius-normal) var(--color-dark-900);

  @extend %gam-special-border;

  :before {
    padding: 1px 0 0;
  }
}

.mobile .gam-dialog-content {
  padding-left: var(--spacing-xl);
  padding-right: var(--spacing-xl);
}

.back-button {
  position: absolute;
  bottom: -16px;
  background-color: var(--color-white-100);
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  cursor: pointer;

  @include hover {
    border: 2px solid var(--color-pink);
  }
}

.prevent .gam-dialog-content {
  padding: var(--spacing-menu);
}
</style>
